import { useState } from 'react';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { MaintCardShell } from './MaintCardShell';
import { ConfirmApplyDialog } from './ConfirmApplyDialog';

export default function DuplicateJobsCard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [confirmGroup, setConfirmGroup] = useState(null);
  const [applying, setApplying] = useState(false);

  const scan = async () => {
    setLoading(true);
    try {
      const res = await api.get('/admin/maintenance/duplicate-jobs/scan');
      setData(res.data);
    } catch (e) { toast.error(e.response?.data?.detail || 'Scan failed'); }
    finally { setLoading(false); }
  };

  const apply = async () => {
    if (!confirmGroup) return;
    setApplying(true);
    try {
      const res = await api.post('/admin/maintenance/duplicate-jobs/apply', {
        job_ids: confirmGroup.duplicates.map((d) => d.id),
        keeper_job_number: confirmGroup.keeper.job_number,
      });
      toast.success(`${res.data.total_updated} duplicate job(s) cancelled`);
      setConfirmGroup(null);
      scan();
    } catch (e) { toast.error(e.response?.data?.detail || 'Apply failed'); }
    finally { setApplying(false); }
  };

  return (
    <MaintCardShell
      title="Duplicate jobs"
      description="Jobs from the same source estimate sharing at least one identical item — a sign the same item was converted to a job more than once."
      testId="dup-jobs"
      count={data ? data.total_duplicate_jobs : null}
      loading={loading}
      onScan={scan}
    >
      {data && data.groups.length > 0 && (
        <div className="space-y-2 max-h-80 overflow-y-auto">
          {data.groups.map((g, idx) => (
            <div key={idx} className="border border-gray-100 rounded-lg p-3 text-xs" data-testid={`dup-jobs-group-${idx}`}>
              <p className="font-semibold text-gray-800">Keeps: {g.keeper.job_number} (${g.keeper.total})</p>
              <p className="text-gray-500 mb-2">Duplicates: {g.duplicates.map((d) => `${d.job_number} ($${d.total})`).join(', ')}</p>
              <Button data-testid={`dup-jobs-group-${idx}-cancel-btn`} size="sm" variant="outline" className="border-red-200 text-red-700" onClick={() => setConfirmGroup(g)}>
                Cancel duplicates in this group
              </Button>
            </div>
          ))}
        </div>
      )}
      {data && data.groups.length === 0 && <p className="text-xs text-emerald-600">No duplicate jobs found.</p>}

      <ConfirmApplyDialog
        open={!!confirmGroup}
        onClose={() => setConfirmGroup(null)}
        onConfirm={apply}
        confirming={applying}
        title="Cancel duplicate jobs"
        summary={confirmGroup ? `This will set status=cancelled (never delete) on: ${confirmGroup.duplicates.map((d) => d.job_number).join(', ')}. ${confirmGroup.keeper.job_number} is kept untouched.` : ''}
      />
    </MaintCardShell>
  );
}
